/**
 * PipelinePage - Primary view of the app. Kanban board for job search tracking.
 * Jobs move through stages: Saved -> Applied -> Interview -> Offer / Rejected.
 * All data persisted to localStorage via Zustand pipeline store.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { usePipelineStore } from '@/store/pipeline.store';
import { pipelineService } from '@/services/pipeline.service';
import { ExtensionPrompt } from '@/components/ExtensionPrompt';
import { useAuth } from '@/hooks';
import {
  PipelineBoard,
  PipelineFilters,
  PipelineEmptyState,
  AddJobModal,
} from '@/components/pipeline';
import type { AddJobFormData } from '@/components/pipeline';
import type { PipelineStatus } from '@/types/pipeline';
import '@/styles/pipeline.css';

export default function PipelinePage() {
  const jobs = usePipelineStore((s) => s.jobs);
  const filters = usePipelineStore((s) => s.filters);
  const sortBy = usePipelineStore((s) => s.sortBy);
  const setFilters = usePipelineStore((s) => s.setFilters);
  const setSortBy = usePipelineStore((s) => s.setSortBy);
  const moveJob = usePipelineStore((s) => s.moveJob);
  const removeJob = usePipelineStore((s) => s.removeJob);
  const fetchFromBackend = usePipelineStore((s) => s.fetchFromBackend);
  const hasFetchedFromBackend = usePipelineStore((s) => s.hasFetchedFromBackend);
  const { isAuthenticated } = useAuth();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Fetch jobs from backend on mount (cloud mode only, once per session)
  useEffect(() => {
    if (isAuthenticated && !hasFetchedFromBackend) {
      fetchFromBackend();
    }
  }, [isAuthenticated, hasFetchedFromBackend, fetchFromBackend]);

  // Filter jobs by search query
  const filteredJobs = useMemo(() => {
    let result = [...jobs];

    // Apply search filter
    const query = filters.search.toLowerCase().trim();
    if (query) {
      result = result.filter(
        (job) =>
          job.title.toLowerCase().includes(query) ||
          job.company.toLowerCase().includes(query)
      );
    }

    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return b.matchScore - a.matchScore;
        case 'company':
          return a.company.localeCompare(b.company);
        case 'date':
        default:
          return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();
      }
    });

    return result;
  }, [jobs, filters.search, sortBy]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setFilters({ search: value });
    },
    [setFilters]
  );

  const handleSortChange = useCallback(
    (value: 'score' | 'date' | 'company') => {
      setSortBy(value);
    },
    [setSortBy]
  );

  const handleMoveJob = useCallback(
    (jobId: string, newStatus: PipelineStatus) => {
      moveJob(jobId, newStatus);
    },
    [moveJob]
  );

  const handleDeleteJob = useCallback(
    (jobId: string) => {
      removeJob(jobId);
    },
    [removeJob]
  );

  const handleAddJob = useCallback(
    (formData: AddJobFormData) => {
      const result = pipelineService.addJobToPipeline({
        vacancyId: crypto.randomUUID(),
        title: formData.title.trim(),
        company: formData.company.trim(),
        location: formData.location.trim() || undefined,
        source: formData.source || undefined,
        url: formData.url.trim() || undefined,
        description: formData.description.trim() || undefined,
        matchScore: 0,
      });

      if (!result.success) {
        console.error('Failed to add job:', result.error);
      }
    },
    []
  );

  const openAddModal = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const hasJobs = jobs.length > 0;

  return (
    <div>
      {/* Page header */}
      <div className="pipeline-header">
        <div>
          <h1 className="pipeline-header__title">Job Pipeline</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Track your job applications from saved to offer. Click a job to generate a tailored CV.
          </p>
        </div>
        {hasJobs && (
          <button
            onClick={openAddModal}
            className="inline-flex items-center justify-center font-medium rounded-lg transition-colors bg-primary text-white hover:bg-primary-dark px-4 py-2 text-sm shadow-sm"
          >
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Job
          </button>
        )}
      </div>

      {hasJobs ? (
        <>
          {/* Filters bar */}
          <PipelineFilters
            search={filters.search}
            sortBy={sortBy}
            totalCount={filteredJobs.length}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
          />

          {/* Extension tip — auto-save from job boards */}
          <div className="mb-4">
            <ExtensionPrompt variant="pipeline-empty" />
          </div>

          {/* Kanban board */}
          <PipelineBoard
            jobs={filteredJobs}
            onMoveJob={handleMoveJob}
            onDeleteJob={handleDeleteJob}
          />
        </>
      ) : (
        /* Empty state */
        <PipelineEmptyState onAddJob={openAddModal} />
      )}

      {/* Add Job Modal */}
      <AddJobModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onAdd={handleAddJob}
      />
    </div>
  );
}
